import React, { useState, useRef } from 'react';

const CLAUSULAS_ISO = [
  { id: '4',  label: '4 — Contexto da Organização' },
  { id: '5',  label: '5 — Liderança' },
  { id: '6',  label: '6 — Planejamento' },
  { id: '7',  label: '7 — Apoio' },
  { id: '8',  label: '8 — Operação' },
  { id: '9',  label: '9 — Avaliação de Desempenho' },
  { id: '10', label: '10 — Melhoria' },
];

const STATUS_AUDIT = ['Planejada', 'Em Andamento', 'Concluída', 'Encerrada'];

const STATUS_CORES = {
  'Planejada':    { bg: 'rgba(96,165,250,0.15)',  border: 'rgba(96,165,250,0.4)',  color: '#1d4ed8' },
  'Em Andamento': { bg: 'rgba(245,158,11,0.15)',  border: 'rgba(245,158,11,0.4)',  color: '#f59e0b' },
  'Concluída':    { bg: 'rgba(16,185,129,0.15)',  border: 'rgba(16,185,129,0.4)',  color: '#10b981' },
  'Encerrada':    { bg: 'rgba(139,148,158,0.15)', border: 'rgba(139,148,158,0.4)', color: '#5f5874' },
};

const NC_CFG = {
  maior: { label: 'NC Maior',                 color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.4)',   icon: '🔴' },
  menor: { label: 'NC Menor',                 color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.4)',  icon: '🟡' },
  om:    { label: 'Oportunidade de Melhoria', color: '#1d4ed8', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.4)',  icon: '🔵' },
  obs:   { label: 'Observação / Apontamento', color: '#6d28d9', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.4)', icon: '🟣' },
};

const uid   = () => String(Date.now() + Math.random());
const fmtDt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

const novoProcesso = () => ({
  id: uid(), nome: '', clausulas: [], ncs: [], anotacoes: '',
});

const novaAuditoria = () => ({
  id: uid(),
  numero: '', titulo: '', norma: 'ISO 9001:2015',
  dataInicio: '', dataFim: '', auditor: '', escopo: '',
  status: 'Planejada',
  processos: [novoProcesso()],
  conclusao: '', anexos: [],
  criadoEm: new Date().toISOString(),
});

// ─── Geração do relatório HTML para impressão/PDF ────────────────────────────
const gerarRelatorioHTML = (a) => {
  const totalNCs = tipo => (a.processos || []).flatMap(p => p.ncs || []).filter(n => n.tipo === tipo).length;

  const secaoProcesso = (p) => {
    const ncsPorTipo = (tipo) => (p.ncs || []).filter(n => n.tipo === tipo);
    const blocoNC = (tipo) => {
      const itens = ncsPorTipo(tipo);
      if (!itens.length) return '';
      const cfg = NC_CFG[tipo];
      return `<h4 style="color:${cfg.color};margin:1rem 0 0.3rem">${cfg.icon} ${cfg.label}s (${itens.length})</h4>
        ${itens.map((nc, i) => `
          <div style="border-left:3px solid ${cfg.color};padding:8px 12px;margin-bottom:8px;background:#fafafa;border-radius:0 6px 6px 0">
            <b>${i+1}. ${nc.descricao || '—'}</b><br>
            ${nc.requisito ? `<small>📌 Requisito: ${nc.requisito}</small><br>` : ''}
            ${nc.evidencia  ? `<small>📎 Evidência: ${nc.evidencia}</small><br>` : ''}
            ${nc.responsavel? `<small>👤 Responsável: ${nc.responsavel}</small><br>` : ''}
            ${nc.prazo      ? `<small>📅 Prazo: ${fmtDt(nc.prazo)}</small><br>` : ''}
            <small style="color:#888">Status: ${nc.statusNC || 'Aberta'}</small>
          </div>`).join('')}`;
    };
    return `
      <div style="border:1px solid #e0e0e0;border-radius:8px;padding:14px;margin-bottom:16px">
        <h3 style="margin:0 0 8px;color:#444">${p.nome || 'Processo sem nome'}</h3>
        ${p.clausulas?.length ? `<p style="font-size:12px;color:#666">Cláusulas: ${p.clausulas.join(', ')}</p>` : ''}
        ${blocoNC('maior')}${blocoNC('menor')}${blocoNC('om')}${blocoNC('obs')}
        ${p.anotacoes ? `<p style="font-size:12px;color:#555;margin-top:8px"><b>Anotações:</b><br>${p.anotacoes}</p>` : ''}
      </div>`;
  };

  const anexosHTML = (a.anexos || []).map(x => {
    if (x.mimeType?.startsWith('image/'))
      return `<div style="margin-bottom:12px"><div style="font-size:12px;color:#555;margin-bottom:4px">📎 ${x.nome}</div><img src="${x.base64}" style="max-width:100%;border-radius:6px;border:1px solid #ddd"/></div>`;
    if (x.mimeType === 'application/pdf')
      return `<div style="margin-bottom:12px"><div style="font-size:12px;color:#555;margin-bottom:4px">📄 ${x.nome}</div><iframe src="${x.base64}" style="width:100%;height:500px;border:1px solid #ddd;border-radius:6px"></iframe></div>`;
    return `<div style="margin-bottom:8px">📎 <a href="${x.base64}" download="${x.nome}">${x.nome}</a></div>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>Relatório — ${a.numero || a.titulo}</title>
  <style>
    body{font-family:Arial,sans-serif;max-width:960px;margin:0 auto;padding:2rem;font-size:13px;color:#222}
    h1{color:#7c3aed;border-bottom:3px solid #7c3aed;padding-bottom:8px}
    h2{color:#444;font-size:15px;margin:1.5rem 0 0.5rem;border-bottom:1px solid #eee;padding-bottom:4px}
    table{width:100%;border-collapse:collapse;margin-bottom:1rem}
    td,th{border:1px solid #ddd;padding:7px 10px;font-size:12px}
    th{background:#f5f5f5;font-weight:600;text-align:left}
    @media print{iframe{page-break-inside:avoid}}
  </style></head><body>
  <h1>Relatório de Auditoria Interna</h1>
  <table>
    <tr><th>Número</th><td>${a.numero||'—'}</td><th>Norma</th><td>${a.norma}</td></tr>
    <tr><th>Título</th><td colspan="3">${a.titulo||'—'}</td></tr>
    <tr><th>Auditor(es)</th><td>${a.auditor||'—'}</td><th>Período</th><td>${fmtDt(a.dataInicio)} a ${fmtDt(a.dataFim)}</td></tr>
    <tr><th>Escopo</th><td colspan="3">${a.escopo||'—'}</td></tr>
    <tr><th>Status</th><td colspan="3">${a.status}</td></tr>
  </table>

  <h2>Resumo das Constatações</h2>
  <table><tr>
    <th style="background:#fee2e2;color:#dc2626">🔴 NC Maiores</th>
    <th style="background:#fef3c7;color:#d97706">🟡 NC Menores</th>
    <th style="background:#dbeafe;color:#2563eb">🔵 Oport. Melhoria</th>
    <th style="background:#ede9fe;color:#7c3aed">🟣 Observações</th>
  </tr><tr style="text-align:center;font-size:22px;font-weight:700">
    <td>${totalNCs('maior')}</td><td>${totalNCs('menor')}</td>
    <td>${totalNCs('om')}</td><td>${totalNCs('obs')}</td>
  </tr></table>

  <h2>Processos Auditados (${(a.processos||[]).length})</h2>
  ${(a.processos||[]).map(secaoProcesso).join('')}

  ${a.conclusao ? `<h2>Conclusão / Parecer Final</h2><p style="line-height:1.7">${a.conclusao}</p>` : ''}
  ${(a.anexos||[]).length ? `<h2>Anexos</h2>${anexosHTML}` : ''}

  <div style="margin-top:2rem;padding-top:1rem;border-top:1px solid #eee;font-size:11px;color:#999;text-align:right">
    Gerado em ${new Date().toLocaleString('pt-BR')} · Kzero Consultoria · ${a.norma}
  </div></body></html>`;
};

// ─── Seletor de cláusulas ─────────────────────────────────────────────────────
const SeletorClausulas = ({ selecionadas = [], onChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {CLAUSULAS_ISO.map(c => {
      const sel = selecionadas.includes(c.id);
      return (
        <button key={c.id} type="button"
          onClick={() => onChange(sel ? selecionadas.filter(x => x !== c.id) : [...selecionadas, c.id])}
          style={{ background: sel ? '#7c3aed' : 'rgba(255,255,255,0.05)', border: `1px solid ${sel ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 16, padding: '5px 13px', fontSize: 12, color: sel ? 'white' : '#5f5874', cursor: 'pointer', fontWeight: sel ? 600 : 400 }}>
          {c.label}
        </button>
      );
    })}
  </div>
);

// ─── Painel de NCs de um processo ────────────────────────────────────────────
const PainelNCs = ({ ncs = [], onChange }) => {
  const [ncForm, setNcForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const salvarNova = () => {
    if (!ncForm?.descricao?.trim()) { alert('Descrição é obrigatória.'); return; }
    onChange([...ncs, { ...ncForm, id: uid(), statusNC: 'Aberta' }]);
    setNcForm(null);
  };

  const salvarEdit = () => {
    onChange(ncs.map(n => n.id === editId ? { ...n, ...editForm } : n));
    setEditId(null);
  };

  const remover = (id) => { if (window.confirm('Remover?')) onChange(ncs.filter(n => n.id !== id)); };

  const inp = (label, field, form, setF, type = 'text') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ fontSize: 10, color: '#5f5874' }}>{label}</label>
      <input type={type} value={form[field] || ''} onChange={e => setF(p => ({ ...p, [field]: e.target.value }))}
        style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 10px', color: '#1c1530', fontSize: 12 }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* resumo badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(NC_CFG).map(([tipo, cfg]) => {
          const qt = ncs.filter(n => n.tipo === tipo).length;
          return (
            <span key={tipo} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '3px 10px', fontSize: 11, color: cfg.color, fontWeight: 600 }}>
              {cfg.icon} {qt} {cfg.label}
            </span>
          );
        })}
      </div>

      {/* botões adicionar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(NC_CFG).map(([tipo, cfg]) => (
          <button key={tipo} type="button"
            onClick={() => setNcForm({ tipo, descricao: '', requisito: '', evidencia: '', responsavel: '', prazo: '', statusNC: 'Aberta' })}
            style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: '5px 12px', fontSize: 11, color: cfg.color, fontWeight: 600, cursor: 'pointer' }}>
            + {cfg.icon} {cfg.label}
          </button>
        ))}
      </div>

      {/* form nova */}
      {ncForm && (
        <div style={{ background: '#ffffff', border: `1px solid ${NC_CFG[ncForm.tipo].border}`, borderRadius: 10, padding: '12px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NC_CFG[ncForm.tipo].color, marginBottom: 10 }}>
            {NC_CFG[ncForm.tipo].icon} Nova {NC_CFG[ncForm.tipo].label}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            <label style={{ fontSize: 10, color: '#5f5874' }}>Descrição *</label>
            <textarea rows={3} value={ncForm.descricao} onChange={e => setNcForm(p => ({ ...p, descricao: e.target.value }))}
              style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '7px 10px', color: '#1c1530', fontSize: 12, resize: 'vertical' }} />
          </div>
          <div className="form-grid-2" style={{ gap: 8 }}>
            {inp('Requisito (ex: 8.4.1)', 'requisito', ncForm, setNcForm)}
            {inp('Evidência Objetiva', 'evidencia', ncForm, setNcForm)}
            {inp('Responsável', 'responsavel', ncForm, setNcForm)}
            {(ncForm.tipo === 'maior' || ncForm.tipo === 'menor') && inp('Prazo', 'prazo', ncForm, setNcForm, 'date')}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" onClick={() => setNcForm(null)}
              style={{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 12px', color: '#5f5874', cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
            <button type="button" onClick={salvarNova}
              style={{ background: NC_CFG[ncForm.tipo].color, color: 'white', border: 'none', borderRadius: 6, padding: '5px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Salvar</button>
          </div>
        </div>
      )}

      {/* lista */}
      {Object.entries(NC_CFG).map(([tipo, cfg]) => {
        const itens = ncs.filter(n => n.tipo === tipo);
        if (!itens.length) return null;
        return (
          <div key={tipo}>
            <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, marginBottom: 6 }}>{cfg.icon} {cfg.label}s ({itens.length})</div>
            {itens.map((nc, i) => (
              <div key={nc.id} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                {editId === nc.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea rows={3} value={editForm.descricao || ''} onChange={e => setEditForm(p => ({ ...p, descricao: e.target.value }))}
                      style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '7px 10px', color: '#1c1530', fontSize: 12, resize: 'vertical' }} />
                    <div className="form-grid-2" style={{ gap: 8 }}>
                      {inp('Requisito', 'requisito', editForm, setEditForm)}
                      {inp('Evidência', 'evidencia', editForm, setEditForm)}
                      {inp('Responsável', 'responsavel', editForm, setEditForm)}
                      {(tipo === 'maior' || tipo === 'menor') && inp('Prazo', 'prazo', editForm, setEditForm, 'date')}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={{ fontSize: 10, color: '#5f5874' }}>Status</label>
                        <select value={editForm.statusNC || 'Aberta'} onChange={e => setEditForm(p => ({ ...p, statusNC: e.target.value }))}
                          style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 10px', color: '#1c1530', fontSize: 12 }}>
                          {['Aberta','Em Tratamento','Encerrada'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" onClick={() => setEditId(null)} style={{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 6, padding: '4px 10px', color: '#5f5874', cursor: 'pointer', fontSize: 11 }}>Cancelar</button>
                      <button type="button" onClick={salvarEdit} style={{ background: cfg.color, color: 'white', border: 'none', borderRadius: 6, padding: '4px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Salvar</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1c1530' }}>{i+1}. {nc.descricao}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" onClick={() => { setEditId(nc.id); setEditForm({ ...nc }); }}
                          style={{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 5, padding: '2px 7px', color: '#5f5874', cursor: 'pointer', fontSize: 11 }}>✏</button>
                        <button type="button" onClick={() => remover(nc.id)}
                          style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 5, padding: '2px 7px', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>🗑</button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 5, flexWrap: 'wrap' }}>
                      {nc.requisito && <span style={{ fontSize: 11, color: '#5f5874' }}>📌 {nc.requisito}</span>}
                      {nc.responsavel && <span style={{ fontSize: 11, color: '#5f5874' }}>👤 {nc.responsavel}</span>}
                      {nc.prazo && <span style={{ fontSize: 11, color: '#5f5874' }}>📅 {fmtDt(nc.prazo)}</span>}
                      <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: nc.statusNC === 'Encerrada' ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.07)', color: nc.statusNC === 'Encerrada' ? '#10b981' : '#5f5874', fontWeight: 600 }}>{nc.statusNC || 'Aberta'}</span>
                    </div>
                    {nc.evidencia && <div style={{ fontSize: 11, color: '#8a849c', marginTop: 3 }}>Evidência: {nc.evidencia}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────
export const Auditorias = ({ auditorias = [], setAuditorias }) => {
  const [view, setView]           = useState('lista');
  const [local, setLocal]         = useState(null);
  const [abaAudit, setAbaAudit]   = useState('info');
  const [processoAberto, setProcessoAberto] = useState(null);
  const fileRef = useRef();

  // Salva a auditoria editada de volta no array do pai
  const salvarNoPai = (dados) => {
    setAuditorias(prev => {
      const lista = Array.isArray(prev) ? prev : [];
      const existe = lista.some(a => a.id === dados.id);
      return existe ? lista.map(a => a.id === dados.id ? dados : a) : [...lista, dados];
    });
  };

  const voltarParaLista = () => {
    if (local) salvarNoPai(local);
    setView('lista');
    setLocal(null);
  };

  const abrirAuditoria = (a) => {
    setLocal({ ...a });
    setAbaAudit('info');
    setProcessoAberto(a.processos?.[0]?.id || null);
    setView('detalhe');
  };

  const upd = (campo, valor) => setLocal(p => ({ ...p, [campo]: valor }));

  const updProcesso = (pid, campo, valor) =>
    setLocal(p => ({ ...p, processos: p.processos.map(pr => pr.id === pid ? { ...pr, [campo]: valor } : pr) }));

  const addProcesso = () => {
    const novo = novoProcesso();
    setLocal(p => ({ ...p, processos: [...(p.processos || []), novo] }));
    setProcessoAberto(novo.id);
  };

  const removerProcesso = (pid) => {
    setLocal(p => {
      const lista = p.processos.filter(pr => pr.id !== pid);
      setProcessoAberto(lista[0]?.id || null);
      return { ...p, processos: lista };
    });
  };

  // ── LISTA ────────────────────────────────────────────────────────────────
  if (view === 'lista') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: '#5f5874' }}>{auditorias.length} auditoria{auditorias.length !== 1 ? 's' : ''}</span>
        <button onClick={() => {
          const nova = novaAuditoria();
          setLocal({ ...nova });
          setAbaAudit('info');
          setProcessoAberto(nova.processos?.[0]?.id || null);
          setView('detalhe');
        }} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + Nova Auditoria
        </button>
      </div>

      {auditorias.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#5f5874', fontSize: 14 }}>
          Nenhuma auditoria registrada. Clique em <strong>+ Nova Auditoria</strong> para começar.
        </div>
      )}

      {[...auditorias].reverse().map(a => {
        const st = STATUS_CORES[a.status] || STATUS_CORES['Planejada'];
        const ncsAll = (a.processos || []).flatMap(p => p.ncs || []);
        const ncM = ncsAll.filter(n => n.tipo === 'maior').length;
        const ncm = ncsAll.filter(n => n.tipo === 'menor').length;
        return (
          <div key={a.id} onClick={() => abrirAuditoria(a)}
            style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 12, padding: '1rem 1.25rem', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {a.numero && <span style={{ fontSize: 11, color: '#5f5874', fontFamily: 'monospace' }}>{a.numero}</span>}
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1530' }}>{a.titulo || 'Sem título'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#5f5874', marginTop: 3 }}>
                  {a.auditor && <span>Auditor: {a.auditor} · </span>}
                  {a.dataInicio && <span>{fmtDt(a.dataInicio)}{a.dataFim ? ` a ${fmtDt(a.dataFim)}` : ''} · </span>}
                  {(a.processos||[]).length} processo{(a.processos||[]).length !== 1 ? 's' : ''} auditado{(a.processos||[]).length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {ncM > 0 && <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 12, padding: '2px 10px', fontSize: 11, color: '#ef4444', fontWeight: 700 }}>🔴 {ncM}</span>}
                {ncm > 0 && <span style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 12, padding: '2px 10px', fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>🟡 {ncm}</span>}
                <span style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 12, padding: '2px 10px', fontSize: 11, color: st.color, fontWeight: 600 }}>{a.status}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── DETALHE ──────────────────────────────────────────────────────────────
  if (view === 'detalhe' && local) {
    const st = STATUS_CORES[local.status] || STATUS_CORES['Planejada'];
    const ABAS_D = [
      { id: 'info',      label: 'ℹ Informações' },
      { id: 'processos', label: `🏭 Processos (${(local.processos||[]).length})` },
      { id: 'conclusao', label: '📝 Conclusão' },
      { id: 'anexos',    label: `📎 Anexos (${(local.anexos||[]).length})` },
    ];

    const inpTxt = (campo, label, type = 'text', full = false) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...(full ? { gridColumn: '1 / -1' } : {}) }}>
        <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600 }}>{label}</label>
        <input type={type} value={local[campo] || ''}
          onChange={e => upd(campo, e.target.value)}
          style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 12px', color: '#1c1530', fontSize: 13 }} />
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={voltarParaLista}
              style={{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 8, padding: '6px 14px', color: '#5f5874', cursor: 'pointer', fontSize: 13 }}>← Voltar</button>
            <div>
              <div style={{ fontSize: 11, color: '#5f5874' }}>{local.numero} · {local.norma}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1530' }}>{local.titulo || 'Sem título'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={local.status} onChange={e => upd('status', e.target.value)}
              style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 8, padding: '6px 12px', color: st.color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {STATUS_AUDIT.map(s => <option key={s}>{s}</option>)}
            </select>
            <button onClick={() => { const w = window.open('','_blank'); w.document.write(gerarRelatorioHTML(local)); w.document.close(); w.print(); }}
              style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              📄 Exportar PDF
            </button>
            <button onClick={() => { if (window.confirm('Excluir esta auditoria?')) { setAuditorias(prev => prev.filter(a => a.id !== local.id)); setView('lista'); setLocal(null); } } }
              style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>🗑</button>
          </div>
        </div>

        {/* abas */}
        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #eeeaf5', paddingBottom: 8, flexWrap: 'wrap' }}>
          {ABAS_D.map(a => (
            <button key={a.id} onClick={() => setAbaAudit(a.id)}
              style={{ background: abaAudit === a.id ? '#7c3aed' : 'transparent', border: `1px solid ${abaAudit === a.id ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 20, color: abaAudit === a.id ? 'white' : '#5f5874', padding: '6px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {a.label}
            </button>
          ))}
        </div>

        {/* ── INFORMAÇÕES ── */}
        {abaAudit === 'info' && (
          <div className="form-grid-2" style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 12, padding: '1.25rem', gap: '1rem' }}>
            {inpTxt('numero', 'Número (ex: AI-2025-001)')}
            {inpTxt('titulo', 'Título da Auditoria')}
            {inpTxt('auditor', 'Auditor(es)')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600 }}>Norma de Referência</label>
              <select value={local.norma} onChange={e => upd('norma', e.target.value)}
                style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 12px', color: '#1c1530', fontSize: 13 }}>
                <option>ISO 9001:2015</option>
                <option>ISO 14001:2015</option>
                <option>ISO 45001:2018</option>
                <option>ISO 19011:2018</option>
                <option>Interna (sem norma)</option>
              </select>
            </div>
            {inpTxt('dataInicio', 'Data Início', 'date')}
            {inpTxt('dataFim', 'Data Fim', 'date')}
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600 }}>Escopo</label>
              <textarea rows={2} value={local.escopo || ''} onChange={e => upd('escopo', e.target.value)}
                style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 12px', color: '#1c1530', fontSize: 13, resize: 'vertical' }} />
            </div>
          </div>
        )}

        {/* ── PROCESSOS AUDITADOS ── */}
        {abaAudit === 'processos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* lista lateral + conteúdo */}
            <div className="aud-proc-wrap" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* coluna de processos */}
              <div className="aud-proc-col" style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(local.processos || []).map(p => (
                  <div key={p.id} onClick={() => setProcessoAberto(p.id)}
                    style={{ background: processoAberto === p.id ? 'rgba(124,58,237,0.15)' : '#ffffff', border: `1px solid ${processoAberto === p.id ? '#7c3aed' : '#eeeaf5'}`, borderRadius: 8, padding: '9px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: processoAberto === p.id ? '#1c1530' : '#5f5874', fontWeight: processoAberto === p.id ? 600 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.nome || 'Novo processo'}
                    </span>
                    {(local.processos||[]).length > 1 && (
                      <button type="button" onClick={e => { e.stopPropagation(); removerProcesso(p.id); }}
                        style={{ background: 'transparent', border: 'none', color: '#8a849c', cursor: 'pointer', fontSize: 13, padding: '0 0 0 4px' }}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={addProcesso}
                  style={{ background: 'transparent', border: '1px dashed #e5e0ef', borderRadius: 8, padding: '7px 12px', color: '#5f5874', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>
                  + Adicionar processo
                </button>
              </div>

              {/* painel do processo selecionado */}
              {(() => {
                const p = (local.processos || []).find(x => x.id === processoAberto);
                if (!p) return <div style={{ flex: 1, color: '#8a849c', fontSize: 13 }}>Selecione um processo.</div>;
                return (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* nome do processo */}
                    <div style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 10, padding: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600, display: 'block', marginBottom: 4 }}>Nome do Processo Auditado *</label>
                          <input value={p.nome || ''} onChange={e => updProcesso(p.id, 'nome', e.target.value)}
                            placeholder="Ex: Gestão de Compras, Recursos Humanos, Produção..."
                            style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 12px', color: '#1c1530', fontSize: 13, width: '100%' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600, display: 'block', marginBottom: 6 }}>Cláusulas ISO 9001:2015 auditadas</label>
                          <SeletorClausulas
                            selecionadas={p.clausulas || []}
                            onChange={val => updProcesso(p.id, 'clausulas', val)}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#5f5874', fontWeight: 600, display: 'block', marginBottom: 4 }}>Anotações do processo</label>
                          <textarea rows={3} value={p.anotacoes || ''} onChange={e => updProcesso(p.id, 'anotacoes', e.target.value)}
                            placeholder="Observações gerais, contexto, pontos positivos..."
                            style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 12px', color: '#1c1530', fontSize: 13, resize: 'vertical', width: '100%' }} />
                        </div>
                      </div>
                    </div>
                    {/* NCs do processo */}
                    <div style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 10, padding: '1rem' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1c1530', marginBottom: 10 }}>Constatações deste processo</div>
                      <PainelNCs
                        ncs={p.ncs || []}
                        onChange={novasNCs => updProcesso(p.id, 'ncs', novasNCs)}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── CONCLUSÃO ── */}
        {abaAudit === 'conclusao' && (
          <div style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 12, padding: '1.25rem' }}>
            <label style={{ fontSize: 12, color: '#5f5874', fontWeight: 600, display: 'block', marginBottom: 8 }}>Conclusão / Parecer Final do Auditor</label>
            <textarea rows={14} value={local.conclusao || ''} onChange={e => upd('conclusao', e.target.value)}
              placeholder="Descreva o parecer final, nível de conformidade do SGQ, pontos fortes e áreas de melhoria..."
              style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '10px 14px', color: '#1c1530', fontSize: 13, resize: 'vertical', width: '100%', lineHeight: 1.7 }} />
          </div>
        )}

        {/* ── ANEXOS ── */}
        {abaAudit === 'anexos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                📎 Anexar Arquivo
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" style={{ display: 'none' }} onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert('Limite: 5 MB por arquivo.'); return; }
                  const reader = new FileReader();
                  reader.onload = ev => {
                    const novo = { id: uid(), nome: file.name, mimeType: file.type, tamanho: file.size, base64: ev.target.result };
                    upd('anexos', [...(local.anexos || []), novo]);
                  };
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }} />
              </label>
              <span style={{ fontSize: 11, color: '#8a849c', marginLeft: 10 }}>PDF, Word, PNG, JPG · máx 5 MB</span>
            </div>

            {(local.anexos || []).length === 0 && (
              <div style={{ textAlign: 'center', color: '#8a849c', padding: '2rem', fontSize: 13 }}>Nenhum anexo ainda.</div>
            )}

            {(local.anexos || []).map(ax => (
              <div key={ax.id} style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #eeeaf5' }}>
                  <div>
                    <span style={{ fontSize: 13, color: '#1c1530', fontWeight: 500 }}>📎 {ax.nome}</span>
                    <span style={{ fontSize: 11, color: '#8a849c', marginLeft: 8 }}>{(ax.tamanho/1024).toFixed(1)} KB</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={ax.base64} download={ax.nome} style={{ background: 'rgba(96,165,250,0.1)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 6, padding: '4px 12px', fontSize: 11, textDecoration: 'none', fontWeight: 600 }}>⬇ Baixar</a>
                    <button onClick={() => { if (window.confirm(`Remover "${ax.nome}"?`)) upd('anexos', (local.anexos||[]).filter(x => x.id !== ax.id)); }}
                      style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>🗑</button>
                  </div>
                </div>
                {ax.mimeType?.startsWith('image/') && <img src={ax.base64} alt={ax.nome} style={{ width: '100%', maxHeight: 500, objectFit: 'contain', background: '#ffffff' }} />}
                {ax.mimeType === 'application/pdf' && <iframe src={ax.base64} title={ax.nome} style={{ width: '100%', height: 600, border: 'none', display: 'block' }} />}
                {(ax.mimeType?.includes('word') || ax.mimeType?.includes('document')) && (
                  <div style={{ padding: '1rem', color: '#5f5874', fontSize: 12 }}>Arquivo Word — use ⬇ Baixar para abrir no computador.</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};
